import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useMutation as rqUseMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useQuery } from "@apollo/client";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import { ForecastFolders } from "constants/forecasts.constants";
import { ForecastActivityType, TYPE_WELLS } from "constants/settings.constants";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectCheckedForecasts,
  selectCheckedGlobalTypeWells,
  selectCheckedTypeWells,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";

import { FOLDERS, FOLDERS_LIGHT } from "api/userArps";
import {
  Rescat,
  ValNavFolderRequest,
  saveValNavFolder,
  updatePerformanceData
} from "api/userForecasts";
import { fetchTypeWellSegmentsAndLengths } from "api/valnavTypeWellLength";

import {
  ArpSegment,
  ForecastConstant,
  ForecastFolder,
  UserArpsItem,
  WellData
} from "models/UserArpsModel";

import { useSelectedProject } from "components/project/projects/hooks";

import { ArpsSegmentToSegmentDto as arpsSegmentToSegmentDto } from "../../arps/utils/arpsUtils";
import "../context/reducer/arpsReducer";
import { groupArpsSegmentsByProduct } from "../utils/groupArpsSegmentsByProduct";
import useForecastFolderFetcher from "./useForecastFolderFetcher";

interface ValNavTypeWellInfo {
  jobNumber: string;
  rescat: Rescat;
  typeWells: string[];
  typeWellUwis: string[];
}

let arpsWasm: typeof import("wasm/arps") = undefined;
import("wasm/arps.js").then((x) => {
  arpsWasm = x;
});

export default function useValNavTypeWellUpdater(type: ForecastActivityType) {
  const checkedTypeWells = useForecastStore(selectCheckedTypeWells);
  const checkedForecastsList = useForecastStore(selectCheckedForecasts);
  const checkedForecasts = type === TYPE_WELLS ? checkedTypeWells : checkedForecastsList;

  const forecastFolders = useSelector(
    (state: RootState) =>
      state.arps[
        type === TYPE_WELLS
          ? ForecastFolders.TypeWellFolders
          : ForecastFolders.ForecastFolders
      ]
  );

  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const setCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);
  const setSyncStateType = useForecastStore((s) => s.setSyncStateType);
  const setSyncStateId = useForecastStore((s) => s.setSyncStateId);

  const { selectedProject: project } = useSelectedProject();
  const { getForecastFoldersFromFoldersData } = useForecastFolderFetcher(type);

  const [valnavError, setValnavError] = useState(null);

  // Use lightweight query for USER_ARPS (forecasts), full query for TYPE_WELLS
  const foldersQuery = type === TYPE_WELLS ? FOLDERS : FOLDERS_LIGHT;

  const { data: foldersData, refetch } = useQuery(foldersQuery, {
    variables: {
      req: {
        projectId: project?.projectId,
        type: type
      }
    },
    skip: !project?.projectId
  });

  const valnavFolderMutation = rqUseMutation(
    "vnfolder",
    (data: ValNavFolderRequest) => {
      return saveValNavFolder(data);
    },
    {
      onSuccess: async (_data, variables) => {
        await refetch();
        // EVA-6855: recalc perf metrics on re-sync, same call the upload path makes.
        // fire-and-forget — a calc failure must not fail the sync (data is already saved),
        // but capture it: a swallowed failure here is a silent recurrence of EVA-6855 (stale metrics).
        updatePerformanceData(variables.folderId).catch((error) => {
          Sentry.withScope((scope) => {
            scope.setContext("updatePerformanceData", { folderId: variables.folderId });
            scope.setLevel("error");
            Sentry.captureException(error);
          });
        });
      }
    }
  );

  function recheck(foldersData: ForecastFolder[], folderId: string, uniqueID: string) {
    const folders = foldersData.filter((f) => f.folderId === folderId);
    if (folders?.length !== 1) {
      return;
    }
    const folder = folders[0];
    const child = folder.children.filter((item) => item.uniqueID === uniqueID);
    if (!child) {
      return;
    }

    // Use checkedGlobalTypeWells as base to preserve color/thickness
    // that may have been updated via the color editor (which only dispatches
    // to checkedGlobalTypeWells, not checkedTypeWells)
    const globalIndex = checkedGlobalTypeWells.findIndex((f) => f.uniqueID === uniqueID);
    if (globalIndex < 0) {
      return;
    }

    // Preserve existing color/thickness from the global state for the refreshed type well,
    // since the API response may not include user-customized values
    const existingEntry = checkedGlobalTypeWells[globalIndex];
    const refreshedChild = {
      ...child[0],
      color: child[0].color ?? existingEntry.color,
      thickness: child[0].thickness ?? existingEntry.thickness
    } as ICheckedForecast;

    const newGlobalChecked = [...checkedGlobalTypeWells];
    newGlobalChecked.splice(globalIndex, 1, refreshedChild);

    setCheckedGlobalTypeWells(newGlobalChecked.filter(Boolean));

    // Also update checkedForecasts (tree state) so both slices stay in sync
    const localIndex = checkedForecasts.findIndex((f) => f.uniqueID === uniqueID);
    if (localIndex >= 0) {
      const newChecked = [...checkedForecasts];
      newChecked.splice(localIndex, 1, refreshedChild);
      if (type === TYPE_WELLS) {
        setCheckedTypeWells(newChecked);
        setCheckedTypeWellKeys(newChecked.map((x) => x.id));
      } else {
        setCheckedForecasts(newChecked);
        setCheckedForecastKeys(newChecked.map((x) => x.id));
      }
    }
  }

  // composeUniqueIdFromWellName = true is only used to sync new client job wells
  // backend will use their name + rescat as uniqueId
  async function updateFolderWithValNavData(
    folder: ForecastFolder,
    valNavTypeWells: ValNavTypeWellInfo,
    modifyingSelectedTypeWell: boolean = false,
    composeUniqueIdFromWellName: boolean = false
  ): Promise<boolean> {
    if (
      !IS_INTERNAL_ENV ||
      type !== TYPE_WELLS ||
      !valNavTypeWells ||
      !valNavTypeWells.jobNumber
    ) {
      return false;
    }
    const rescat = valNavTypeWells.rescat ?? (folder.reserveCategory as Rescat);
    const result = await fetchTypeWellSegmentsAndLengths(
      valNavTypeWells.jobNumber,
      rescat,
      valNavTypeWells.typeWells
    );
    const { lengths, segments, constants } = result;

    const manuallyOverrideDi = [];
    if (arpsWasm) {
      for (const key of Object.keys(segments)) {
        const arpsGroupedByProduct = groupArpsSegmentsByProduct(segments[key]);
        Object.keys(arpsGroupedByProduct).forEach((product) => {
          const productSegments = arpsGroupedByProduct[product];
          const segmentDtos = arpsSegmentToSegmentDto(arpsWasm, productSegments);
          const result = arpsWasm.calculateDi(segmentDtos);
          if (result.length == productSegments.length) {
            for (let i = 0; i < result.length; i++) {
              const currentDi = productSegments[i].di;
              const calculatedDi = result[i].di;
              const diffDiPercent =
                Math.abs(Math.abs(currentDi - calculatedDi) / currentDi) * 100.0;
              if (diffDiPercent > 1) {
                // Same objects as in segments[key], so this updates the payload.
                productSegments[i].di = calculatedDi;
                manuallyOverrideDi.push(
                  `${key}: ${currentDi.toFixed(8)} -> ${calculatedDi.toFixed(8)}`
                );
              }
            }
          }
        });
      }
      //let user know that Di has been recalculated
      if (manuallyOverrideDi.length > 0) {
        toast.warning(
          <div>
            Detected Di that differs from the database: <br />
            {manuallyOverrideDi.join(", ")}
          </div>
        );
      }
    }

    const folderName = folder.folderName;
    const foundFolder = foldersData.folders.filter((f) => f.name === folderName);
    const typeWells = (valNavTypeWells?.typeWells ?? []).map((tw) => tw);
    const typeWellUwis = (valNavTypeWells?.typeWellUwis ?? []).map((tw) => tw);
    const wellData: { [id: string]: WellData } = {};
    const wellKeysToUwiMap: { [wellName: string]: string } = {};
    for (let i = 0; i < typeWells.length; i++) {
      wellKeysToUwiMap[typeWells[i]] = typeWellUwis[i];
    }
    // When modifying the selected type well, send over old type well to make sure we update the existing and
    // not create new type well.
    const oldTypeWells =
      modifyingSelectedTypeWell &&
      selectedTypeWell &&
      typeWellUwis.length &&
      selectedTypeWell.uniqueID !== typeWellUwis[0]
        ? [selectedTypeWell.uniqueID]
        : [];

    // replace segments and constants keys from well name to unique Id
    const segmentsToSend: { [id: string]: ArpSegment[] } = {};
    const constantsToSend: { [id: string]: ForecastConstant[] } = {};
    for (const key of Object.keys(segments)) {
      let uniqueId = "";
      if (composeUniqueIdFromWellName) {
        uniqueId = key + " (" + rescat + ")";
      } else {
        uniqueId = wellKeysToUwiMap[key];
      }
      segmentsToSend[uniqueId] = segments[key];
      constantsToSend[uniqueId] = constants[key];
      // Index lengths by the current well's key so each well keeps its own
      // Hz Length. Using the first length for every well caused EVA-6612.
      wellData[uniqueId] = {
        HzLength: lengths[key] ?? null,
        Proppant: null,
        Stage: null
      } as WellData;
    }
    if (foundFolder.length > 0) {
      for (const fc of foundFolder[0].forecasts) {
        let uid = "";
        if (composeUniqueIdFromWellName) {
          uid = fc.name + " (" + rescat + ")";
        } else {
          uid = wellKeysToUwiMap[fc.name];
        }
        if (!wellData[uid]) {
          wellData[uid] = fc.wellData;
        }
      }
    }

    const request: ValNavFolderRequest = {
      folderId: folder.folderId,
      projectId: project?.projectId,
      oldForecasts: oldTypeWells,
      forecasts: typeWells,
      forecastIds: typeWellUwis,
      segments: segmentsToSend,
      constants: constantsToSend,
      wellData,
      reserveCategory: rescat,
      source: valNavTypeWells.jobNumber,
      composeUniqueIdFromWellName
    };
    await valnavFolderMutation.mutateAsync(request);
    return true;
  }

  async function onUpdateValNavTypeWell(fcstTwNode?: UserArpsItem) {
    try {
      setSyncStateType("typeWell");
      setValnavError(null);
      const node = fcstTwNode ?? selectedTypeWell;
      setSyncStateId(
        fcstTwNode
          ? `${node.folderId}: ${fcstTwNode.uniqueID}`
          : `${node.folderId}: ${selectedTypeWell?.uniqueID}`
      );
      const folder = forecastFolders.filter((f) => f.folderId === node.folderId);

      if (!folder || folder.length === 0 || !node.source) {
        return;
      }
      const updateOk = await updateFolderWithValNavData(folder[0], {
        jobNumber: node.source,
        rescat: node.reserveCategory as Rescat,
        typeWells: [node.title],
        typeWellUwis: [node.uniqueID]
      });
      if (!updateOk) {
        setValnavError("Unable to refresh type well with valnav.");
        return;
      }
      //update forecast folders
      const result = await refetch({
        req: {
          projectId: project?.projectId,
          type: type
        }
      });

      if (!result?.data) {
        setValnavError("Unable to refresh type well with valnav.");
        return;
      }
      const foldersData = getForecastFoldersFromFoldersData(result?.data, type);
      recheck(foldersData, node.folderId, node.uniqueID);
    } catch (err) {
      setValnavError(err?.data ?? "An error occurred.");
    } finally {
      setSyncStateType(null);
      setSyncStateId(null);
    }
  }

  return {
    onUpdateValNavTypeWell,
    updateFolderWithValNavData,
    valnavError,
    setValnavError
  };
}
